/**
 * The statutory year for a South African trust — the recurring SARS dates a
 * trust is measured against, computed live against `now`.
 *
 * WHY THIS EXISTS (2026-08-06). The trust overview claimed "In good standing ·
 * nothing overdue" off two inputs: the FICA review date and the awaiting-signature
 * count. Nothing anywhere in the product knew about the ITR12T, provisional tax,
 * or the February year end — those dates existed only as static prose on
 * `routes/app/trusts/$id/tax.tsx`. A trust that had never filed a return showed
 * a green dot.
 *
 * THE HONESTY RULE THIS MODULE ENCODES. Elreign does not file returns and cannot
 * see SARS eFiling. It knows when a return is DUE; it never knows whether the
 * firm's accountant filed it. So every obligation carries `observed`:
 *
 *   observed: false  → SARS filings. A passed date reads "was due 19 Jan 2027",
 *                      a statement about the calendar. It must NEVER read
 *                      "overdue", which would be an accusation about the firm's
 *                      conduct that Elreign has no basis to make.
 *   observed: true   → Elreign's own workflow (the FICA review cycle). Elreign
 *                      owns this date, so "overdue" is a fact it can stand behind.
 *
 * This is the same class of defect Beno caught twice before: paper resolutions
 * rendered as "fully signed on the audit chain" for signatures Elreign never
 * witnessed. A date we can compute is not a fact about what someone did.
 *
 * Dates are pure calendar dates ('YYYY-MM-DD') with no time-of-day component.
 * `daysUntil` (2026-08-22 fix) reads "today" as a SAST (South Africa Standard
 * Time, UTC+2, no daylight saving — so a fixed offset is always correct)
 * calendar date, not a UTC one. Before this fix, every countdown rolled over
 * at midnight UTC instead of midnight Johannesburg time — SAST is two hours
 * ahead, so between 00:00 and 02:00 SAST the UTC clock still reads the
 * PREVIOUS day, and every obligation (including one due that very day) showed
 * one day more runway than actually remained. The SARS deadlines below mirror
 * the prose already shipped on the tax screen — if one changes, change both.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Warning window before a due date. Matches the 30-day threshold the trust
 *  overview already used for the FICA review, so the two agree. */
export const OBLIGATION_DUE_SOON_DAYS = 30;

/** Day-of-January SARS has set for the ITR12T trust-return deadline.
 *  SOURCE: SARS notice — provisional taxpayers incl. trusts; verify each
 *  season. NOT a fixed statutory date like the Feb tax year end: SARS
 *  publishes it fresh by notice every filing season and has used 19–20
 *  January the last two trust filing seasons (2025, 2026) — not the
 *  31 January this module hardcoded until 2026-08-22. Re-verify against the
 *  current season's notice once one rolls; changing it is one line here. */
export const ITR12T_DEADLINE_JANUARY_DAY = 19;

export type ObligationId =
  | 'irp6-first'
  | 'irp6-second'
  | 'tax-year-end'
  | 'irp6-third'
  | 'it3t'
  | 'itr12t'
  | 'fica-review';

export type ObligationState = 'upcoming' | 'due_soon' | 'passed' | 'overdue';

export interface StatutoryObligation {
  id: ObligationId;
  /** Which year of assessment this occurrence belongs to (the calendar year the
   *  tax year ends in). Null for non-SARS items like the FICA review. */
  yearOfAssessment: number | null;
  label: string;
  /** One line of plain English: what it is and why the date bites. */
  detail: string;
  /** Calendar due date, 'YYYY-MM-DD'. */
  dueOn: string;
  /** Whole calendar days until due. 0 = due today, negative = past. */
  daysRemaining: number;
  /** False when Elreign cannot see whether the obligation was met — see the
   *  honesty rule in the module header. Drives "was due" vs "overdue". */
  observed: boolean;
  state: ObligationState;
}

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function iso(y: number, month1: number, day: number): string {
  const mm = String(month1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}

/** Last day of the SA tax year ending in calendar year `y` — 28 Feb, or 29 in a
 *  leap year. The year of assessment runs 1 March (y-1) to this date. */
export function taxYearEnd(y: number): string {
  return iso(y, 2, isLeapYear(y) ? 29 : 28);
}

/** SAST (South Africa Standard Time) is a fixed UTC+2 — South Africa has not
 *  observed daylight saving since 1943, so, unlike most timezone math, a
 *  constant offset is always correct here; no tz database needed. */
const SAST_OFFSET_MS = 2 * 60 * 60 * 1000;

/** Whole calendar days from `nowMs` to a 'YYYY-MM-DD' date, floor-free: both
 *  sides resolve to an exact UTC midnight, so the division is a whole number.
 *
 *  `dueOn` is already a bare calendar date with no time-of-day, so it needs no
 *  timezone conversion — "the SAST calendar date" of a value with no clock
 *  time is just itself. `nowMs`, by contrast, is a real instant, and reading
 *  its UTC date directly (the pre-2026-08-22 bug) answers "what date is it in
 *  Greenwich", not "what date is it in Johannesburg" — wrong for two hours
 *  out of every day (00:00–02:00 SAST, while UTC still shows the previous
 *  date), including the two hours after a deadline has actually arrived. Both
 *  ends of the subtraction below are therefore anchored the same way: read
 *  the calendar date first (shifting `nowMs` by the fixed SAST offset before
 *  taking its date, since a bare date needs no shift), then take that date's
 *  UTC midnight — so the difference is a pure SAST calendar-day count. */
export function daysUntil(dueOn: string, nowMs: number): number {
  const due = Date.parse(`${dueOn}T00:00:00.000Z`);
  const sastNow = new Date(nowMs + SAST_OFFSET_MS);
  const today = Date.UTC(sastNow.getUTCFullYear(), sastNow.getUTCMonth(), sastNow.getUTCDate());
  return Math.round((due - today) / DAY_MS);
}

function stateFor(daysRemaining: number, observed: boolean): ObligationState {
  if (daysRemaining < 0) return observed ? 'overdue' : 'passed';
  if (daysRemaining <= OBLIGATION_DUE_SOON_DAYS) return 'due_soon';
  return 'upcoming';
}

function build(
  id: ObligationId,
  yearOfAssessment: number | null,
  label: string,
  detail: string,
  dueOn: string,
  observed: boolean,
  nowMs: number,
): StatutoryObligation {
  const daysRemaining = daysUntil(dueOn, nowMs);
  return {
    id,
    yearOfAssessment,
    label,
    detail,
    dueOn,
    daysRemaining,
    observed,
    state: stateFor(daysRemaining, observed),
  };
}

/** How a year of assessment reads to a human: "the year ended 28 Feb 2026". */
export function yearOfAssessmentLabel(y: number): string {
  return `year ended ${isLeapYear(y) ? 29 : 28} Feb ${y}`;
}

/**
 * The six SARS dates attached to one year of assessment `y` (the tax year
 * 1 March y-1 → 28/29 Feb y). None of these are observable by Elreign.
 */
export function obligationsForYearOfAssessment(y: number, nowMs: number): StatutoryObligation[] {
  const yearEnd = taxYearEnd(y);
  const yoa = yearOfAssessmentLabel(y);
  return [
    build(
      'irp6-first',
      y,
      'Provisional tax: first period (IRP6)',
      `First IRP6 estimate for the ${yoa}, covering March to August.`,
      iso(y - 1, 8, 31),
      false,
      nowMs,
    ),
    build(
      'tax-year-end',
      y,
      'Tax year end: distribution resolutions',
      'Trustee resolutions vesting income in beneficiaries must be dated on or before year end, or the income is taxed in the trust at 45%.',
      yearEnd,
      false,
      nowMs,
    ),
    build(
      'irp6-second',
      y,
      'Provisional tax: second period (IRP6)',
      `Second IRP6 estimate for the ${yoa}, due on the last day of the tax year.`,
      yearEnd,
      false,
      nowMs,
    ),
    build(
      'irp6-third',
      y,
      'Provisional tax: third (top-up) payment',
      `Voluntary top-up for the ${yoa}; pays down interest on a shortfall.`,
      iso(y, 9, 30),
      false,
      nowMs,
    ),
    build(
      'it3t',
      y,
      'IT3(t) beneficial-interest submission',
      `Third-party data submission to SARS disclosing each beneficiary's vested/vesting interest for the ${yoa}. This is the data behind the register on this screen.`,
      iso(y, 9, 30),
      false,
      nowMs,
    ),
    build(
      'itr12t',
      y,
      'ITR12T annual trust return',
      `The trust's income tax return for the ${yoa}. eFiling for trusts typically opens in late September.`,
      iso(y + 1, 1, ITR12T_DEADLINE_JANUARY_DAY),
      false,
      nowMs,
    ),
  ];
}

export interface StatutoryCalendarInput {
  nowMs?: number;
  /** The trust's stored next FICA review date ('YYYY-MM-DD' or ISO datetime).
   *  Omitted/null when the trust has none — no FICA row is emitted. */
  ficaReviewOn?: string | null;
}

/**
 * Every obligation in a window around `now`, soonest first.
 *
 * The window spans three years of assessment (last, current, next) so a firm
 * looking in, say, October sees both the ITR12T that closed in January and the
 * one opening next July. Callers filter — `upcomingObligations` is the common case.
 */
export function statutoryCalendar(input: StatutoryCalendarInput = {}): StatutoryObligation[] {
  const nowMs = input.nowMs ?? Date.now();
  // SAST calendar date for the year-of-assessment cutover — same class of bug
  // as daysUntil's (fixed 2026-08-22): raw UTC put the 1 March / 1 January
  // boundaries two hours late for South African users.
  const now = new Date(nowMs + SAST_OFFSET_MS);
  // The year of assessment currently running: Jan/Feb still belong to the tax
  // year that ends this calendar year; March onward belongs to the next one.
  const currentYoa =
    now.getUTCMonth() >= 2 ? now.getUTCFullYear() + 1 : now.getUTCFullYear();

  const items: StatutoryObligation[] = [];
  for (const y of [currentYoa - 1, currentYoa, currentYoa + 1]) {
    items.push(...obligationsForYearOfAssessment(y, nowMs));
  }

  const fica = normaliseDate(input.ficaReviewOn);
  if (fica) {
    items.push(
      build(
        'fica-review',
        null,
        'FICA review',
        "Elreign's own periodic re-verification of the trust's parties.",
        fica,
        true,
        nowMs,
      ),
    );
  }

  return items.sort((a, b) => (a.dueOn < b.dueOn ? -1 : a.dueOn > b.dueOn ? 1 : 0));
}

/** Accepts 'YYYY-MM-DD' or a full ISO datetime; returns the calendar date, or
 *  null when unusable. Never throws — legacy rows carry junk.
 *  A full timestamp is resolved to its SAST calendar date (2026-08-22, same
 *  class as the daysUntil fix): a review stored as 22:30Z belongs to the NEXT
 *  South African day, and slicing raw UTC put it a day early. Bare dates pass
 *  through untouched — a date with no clock needs no timezone. */
function normaliseDate(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value.length === 10) {
    return Number.isNaN(Date.parse(`${value}T00:00:00.000Z`)) ? null : value;
  }
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return new Date(ms + SAST_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * The next `count` obligations, soonest first.
 *
 * Keeps anything still ahead, PLUS anything observed that has gone past — an
 * overdue FICA review is the most urgent row on the card, and an earlier cut
 * here dropped it on the floor: filtering on `daysRemaining >= 0` alone hid
 * precisely the item a trustee needed to see. Unobservable SARS dates that have
 * passed stay out; Elreign cannot know whether they were met, so listing them
 * forever would be noise wearing the costume of a warning.
 */
export function upcomingObligations(
  input: StatutoryCalendarInput = {},
  count = 4,
): StatutoryObligation[] {
  return statutoryCalendar(input)
    .filter((o) => o.daysRemaining >= 0 || o.observed)
    .slice(0, count);
}

/** The single most pressing obligation, or null when the window holds none. */
export function nextObligation(input: StatutoryCalendarInput = {}): StatutoryObligation | null {
  return upcomingObligations(input, 1)[0] ?? null;
}

/** Sort rank for a mixed queue: most urgent first. */
export function obligationRank(state: ObligationState): number {
  switch (state) {
    case 'overdue': return 0;
    case 'due_soon': return 1;
    case 'upcoming': return 2;
    case 'passed': return 3;
  }
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "31 Jan 2027" from '2027-01-31'. */
export function formatObligationDate(dueOn: string): string {
  const [y, m, d] = dueOn.split('-');
  const month = MONTHS[Number(m) - 1];
  if (!y || !month || !d) return dueOn;
  return `${Number(d)} ${month} ${y}`;
}

/**
 * How the product phrases a deadline in days. ONE function, because the
 * cohesion pass (2026-09-15) put a dated urgency chip on the shared row and a
 * second copy of this wording would be a second answer to the same question:
 * the contract's whole point about urgency is that the chip STATES THE DEADLINE
 * instead of shouting a severity, and two phrasings of "two days late" on two
 * screens is the drift this pass exists to remove.
 *
 * Negative is late, 0 is today, positive is ahead. It says nothing about
 * whether being late is a fault: `obligationBadgeLabel` below decides that,
 * because for a statutory date the product must not say "overdue" about an
 * obligation it cannot observe.
 */
export function deadlinePhrase(daysRemaining: number): string {
  if (daysRemaining < 0) {
    const late = Math.abs(daysRemaining);
    return `Overdue by ${late} ${late === 1 ? 'day' : 'days'}`;
  }
  if (daysRemaining === 0) return 'Due today';
  return `Due in ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`;
}

/**
 * The badge a row shows. Note the asymmetry, which is the whole point of the
 * module: an unobservable obligation whose date has passed reads "Was due …",
 * never "overdue". Only Elreign's own FICA cycle earns that word.
 */
export function obligationBadgeLabel(o: StatutoryObligation): string {
  const d = o.daysRemaining;
  switch (o.state) {
    case 'overdue':
      return deadlinePhrase(d);
    case 'passed':
      return `Was due ${formatObligationDate(o.dueOn)}`;
    case 'due_soon':
      return deadlinePhrase(d);
    case 'upcoming':
      return `Due ${formatObligationDate(o.dueOn)}`;
  }
}
